import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Parcel, ParcelApiResponse } from "../services/parcelTypes";

type ParcelContextValue = {
    parcels: Parcel[];
    total: number;
    byPin: Map<string, Parcel>;
    loading: boolean;
    error: string;
    refetch: () => void;
    refreshParcel: (pin: string) => Promise<Parcel>;
};

type UpdateParcelsParams = {
    ids: string[];
    active: boolean;
};

const ParcelContext = createContext<ParcelContextValue | null>(null);

const PARCEL_ENDPOINT = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getparcels`;
const PARCEL_DETAIL_ENDPOINT = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getparcelbypin`;

export function ParcelProvider({
    endpoint = PARCEL_ENDPOINT,
    children,
    }: React.PropsWithChildren<{ endpoint?: string }>) {
    // Full unfiltered list — source of truth from the API
    const allParcelsRef = useRef<Parcel[]>([]);
    const [allParcels, setAllParcels] = useState<Parcel[]>([]);

    const [parcels, setParcels] = useState<Parcel[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");

    const fetchParcels = async (signal?: AbortSignal) => {
        setLoading(true);
        setError("");

        try {
            const res = await fetch(endpoint, { signal });
            if (!res.ok) throw new Error(`Request failed (${res.status})`);

            const json = (await res.json()) as Partial<ParcelApiResponse>;
            const list = Array.isArray(json?.data) ? (json.data as Parcel[]) : [];

            console.log('parcels list:', list);

            // Store the full list for filtering later
            allParcelsRef.current = list;
            setAllParcels(list);

            setParcels(list);
            setTotal(typeof json?.results === "number" ? json.results : list.length);
        } catch (e: any) {
            if (e?.name === "AbortError") return;
            setError(e?.message ?? "Failed to load parcels.");
        } finally {
            setLoading(false);
        }
    };

    const refreshParcel = async (pin: string): Promise<Parcel> => {
        const res = await fetch(`${PARCEL_DETAIL_ENDPOINT}/${encodeURIComponent(pin)}`);
        if (!res.ok) throw new Error(`Failed to refresh parcel (${res.status})`);

        const json = (await res.json()) as Partial<ParcelApiResponse>;
        console.log("refreshParcel response:", json);
        const updatedParcel = json?.data as Parcel | undefined;

        if (!updatedParcel?.pin) {
            throw new Error("Parcel refresh returned invalid data.");
        }

        // Update source-of-truth full list
        const nextAll = [...allParcelsRef.current];
        const allIndex = nextAll.findIndex((p) => p?.pin === updatedParcel.pin);

        if (allIndex >= 0) {
            nextAll[allIndex] = updatedParcel;
        } else {
            nextAll.push(updatedParcel);
        }

        allParcelsRef.current = nextAll;
        setAllParcels(nextAll);

        // Update currently visible list
        setParcels((prev) => {
            const visibleIndex = prev.findIndex((p) => p?.pin === updatedParcel.pin);

            if (visibleIndex >= 0) {
                const next = [...prev];
                next[visibleIndex] = updatedParcel;
                return next;
            }

            return prev;
        });

        return updatedParcel;
    };

    // Fetch parcels on mount / endpoint change
    useEffect(() => {
        const controller = new AbortController();
        fetchParcels(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint]);

    // Listen for update-parcels messages from the parent application
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Validate message shape before acting on it
            if (
                !event.data ||
                typeof event.data !== "object" ||
                event.data.message !== "update-parcels"
            ) {
                return;
            }
 
            const { ids, active } = event.data.params as UpdateParcelsParams & { type: string };
 
            if (!active || !Array.isArray(ids) || ids.length === 0) {
                // Not active or no IDs — restore the full list
                setParcels(allParcelsRef.current);
                setTotal(allParcelsRef.current.length);
                return;
            }
 
            const idSet = new Set(ids.map(String));
            const filtered = allParcelsRef.current.filter(
                (p) => p?.pin && idSet.has(String(p.pin))
            );
 
            console.log(
                `update-parcels: filtering ${allParcelsRef.current.length} parcels → ${filtered.length} matched`
            );
 
            setParcels(filtered);
            setTotal(filtered.length);
        };
 
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);


    const byPin = useMemo(() => {
        const map = new Map<string, Parcel>();
        for (const p of allParcels) {
            if (p?.pin) map.set(p.pin, p);
        }
        return map;
    }, [allParcels]);

    const value = useMemo<ParcelContextValue>(
        () => ({
        parcels,
        total,
        byPin,
        loading,
        error,
        refetch: () => fetchParcels(undefined),
        refreshParcel,
        }),
        [parcels, total, byPin, loading, error]
    );

    return <ParcelContext.Provider value={value}>{children}</ParcelContext.Provider>;
}

export function useParcels(): ParcelContextValue {
    const ctx = useContext(ParcelContext);
    if (!ctx) throw new Error("useParcels must be used within <ParcelProvider />");
    return ctx;
}
